import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import DefaultLayout from '../presentation/layouts/default';
import DemosPresenter from '../presentation/presenters/demos-presenter';
import { GetPageDefaultsInteractor } from '../domain/usecases/get-page-defaults-interactor';
import { SiteInfoService } from '../data/services/data-site-info-service';
import { ListClientsInteractor } from '../domain/usecases/list-clients-interactor';
import { ClientService } from '../data/services/data-client-service';
import Client from '../domain/models/client';
import PropertyList, { FilterType } from '../domain/models/property-list';
import { ListPropertyTypesInteractor } from '../domain/usecases/list-property-types-interactor';
import { PropertyListService } from '../data/services/data-property-list-service';
import { GetServerSideProps } from 'next';
import useDebounce from '../utils/use-debounce';

type PropsType = {
  defaultFilter: FilterType;
  defaultTextFilter: string;
};

export default function DemosPage(props: PropsType) {
  const { defaultFilter, defaultTextFilter } = props;
  const [siteInfo, setSiteInfo] = useState(null);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [filter, setFilter] = useState<FilterType>({});
  const [textFilter, setTextFilter] = useState(null);
  const debouncedTextFilter = useDebounce(textFilter, 500);
  const [propertyLists, setPropertyLists] = useState<PropertyList[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  useEffect(() => {
    fetchPageDefaults();
    loadPropertyLists();
    setFilter(defaultFilter);
    setTextFilter(defaultTextFilter);
  }, []);

  useEffect(() => {
    if (propertyLists?.length > 0) {
      applyFilter();
    }
    return () => {
      clearTimeout(searchTimeout);
    };
  }, [filter, debouncedTextFilter, propertyLists]);

  const fetchPageDefaults = async () => {
    const getPageDefaultsInteractor = new GetPageDefaultsInteractor(new SiteInfoService());
    const siteInfo = await getPageDefaultsInteractor.getSiteInfo();
    setSiteInfo(siteInfo);
  };

  const loadPropertyLists = async () => {
    const listPropertyTypesInteractor = new ListPropertyTypesInteractor(new PropertyListService());
    const propertyLists = await listPropertyTypesInteractor.getPropertyLists();
    const filtered = propertyLists.filter((p) => p.title != 'Union Status');
    bringToTop(filtered, 'Language');
    bringToTop(filtered, 'Accent');
    bringToTop(filtered, 'Ethnicity');
    bringToTop(filtered, 'Age');
    bringToTop(filtered, 'Sex');
    setPropertyLists(filtered);
  };

  const bringToTop = (list: PropertyList[], title: string) => {
    const indexSex = list.findIndex((p) => p.title == title);
    if (indexSex >= 0) {
      const records = list.splice(indexSex, 1);
      if (records && records.length > 0) {
        list.unshift(records[0]);
      }
    }
  };

  const applyFilter = async () => {
    setLoading(true);
    clearTimeout(searchTimeout);
    setSearchTimeout(
      setTimeout(async () => {
        const listClientsInteractor = new ListClientsInteractor(new ClientService());
        const clients = await listClientsInteractor.listClients(filter, debouncedTextFilter);
        // if (debouncedTextFilter && debouncedTextFilter.length > 0) {
        //   const filteredClients = clients.filter(
        //     // filter client name
        //     (c: Client) => c.name.toLowerCase().indexOf(debouncedTextFilter.toLowerCase()) >= 0
        //   );
        //   setFilteredClients(filteredClients);
        // } else {
        setFilteredClients(clients);
        // }
        setLoading(false);
      }, 1000)
    );
    applyURLUpdate();
  };

  const applyURLUpdate = () => {
    let newQuery = '';
    // comment out below for now, we need to use Label when applying default filter
    // const trimmedFilter = {}
    // Object.keys(filter).forEach(key => {
    //   trimmedFilter[key] = filter[key].map(f => {
    //     return { value: f.value }
    //   })
    // });
    // const filterString = encodeURIComponent(JSON.stringify(trimmedFilter))
    const filterString = encodeURIComponent(JSON.stringify(filter));
    if (Object.keys(filter).length > 0) {
      newQuery += `?filter=${filterString}`;
    }
    const textFilterString = debouncedTextFilter || '';
    if (textFilterString.length > 0) {
      if (newQuery.length > 0) {
        newQuery += `&text_filter=${textFilterString}`;
      } else {
        newQuery += `?text_filter=${textFilterString}`;
      }
    }
    const refresh =
      window.location.protocol + '//' + window.location.host + window.location.pathname + newQuery;
    window.history.pushState({ path: refresh }, '', refresh);
  };

  const onFilterChanged = (filter: FilterType) => {
    setFilter(filter);
  };

  const onTextFilterChanged = (textFilter: string) => {
    setTextFilter(textFilter);
  };

  return (
    <DefaultLayout siteInfo={siteInfo}>
      <Head>
        <title>Demos</title>
        <link
          rel="stylesheet"
          href="https://maxcdn.bootstrapcdn.com/bootstrap/3.3.7/css/bootstrap.min.css"
          integrity="sha384-BVYiiSIFeK1dGmJRAkycuHAHRg32OmUcww7on3RYdg4Va+PmSTsz/K68vbdEjh4u"
          crossOrigin="anonymous"
        />
      </Head>
      <DemosPresenter
        clients={filteredClients}
        propertyLists={propertyLists}
        onFilterChanged={onFilterChanged}
        onTextFilterChanged={onTextFilterChanged}
        defaultSelected={defaultFilter}
        defaultTextFilter={defaultTextFilter}
        loading={loading}
      />
    </DefaultLayout>
  );
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { filter, text_filter } = ctx.query;
  let defaultFilter = {};
  const defaultTextFilter = text_filter || '';
  if (filter) {
    defaultFilter = JSON.parse(decodeURIComponent(filter.toString()));
  }
  return {
    props: {
      defaultFilter,
      defaultTextFilter
    }
  };
};
